// src/App.js

import React, { useState, useEffect } from "react";
import { Routes, Route } from "react-router-dom";
import * as BooksAPI from "./BooksAPI";
import "./App.css";

// Import all required page components from the components folder
import MainPage from "./components/MainPage";
import SearchPage from "./components/SearchPage";
import BookDetail from "./components/BookDetail"; // Corrected import

const BooksApp = () => {
    // --- STATE MANAGEMENT --- (Must be declared first)
    const [books, setBooks] = useState([]);
    const [mapOfIdToBooks, setMapOfIdToBooks] = useState(new Map());
    const [searchBooks, setSearchBooks] = useState([]);
    const [query, setQuery] = useState("");

    // --- EFFECT: Get All Books on Mount (for Main Page) ---
    useEffect(() => {
        BooksAPI.getAll().then((data) => {
            setBooks(data);
            setMapOfIdToBooks(createMapOfBooks(data));
        });
    }, []);

    // --- EFFECT: Handle Search Query ---
    useEffect(() => {
        let isActive = true;
        if (query) {
            BooksAPI.search(query).then((data) => {
                // Handle no results or error (e.g., invalid query)
                if (data.error || !Array.isArray(data)) {
                    if (isActive) setSearchBooks([]);
                } else {
                    if (isActive) setSearchBooks(data);
                }
            });
        } else {
            // Clear search results when query is empty
            setSearchBooks([]);
        }

        return () => {
            isActive = false;
        };
    }, [query]);

    // --- UTILITY: Create Map of Books for Quick Lookup ---
    const createMapOfBooks = (books) => {
        const map = new Map();
        books.forEach((book) => map.set(book.id, book));
        return map;
    };

    // --- HANDLER: Update Book Shelf (Core Logic) ---
    const updateBookShelf = (book, whereTo) => {
        // 1. Optimistically update the UI state immediately
        // Remove the book from its old position (if it was already in 'books')
        const updatedBooks = books.filter((b) => b.id !== book.id);

        if (whereTo !== "none") {
            // If setting to a new shelf, ensure the shelf property is updated on the book object
            // Use the book object from the main state if available, or the one passed in (e.g., from search)
            const bookOnShelf = books.find((b) => b.id === book.id) || book;
            bookOnShelf.shelf = whereTo;
            updatedBooks.push(bookOnShelf);
        }

        // 2. Update the application state and the lookup map
        setBooks(updatedBooks);
        setMapOfIdToBooks(createMapOfBooks(updatedBooks)); 

        // 3. Call the API to persist the change
        BooksAPI.update(book, whereTo);
    };

    // --- UTILITY: Merge Search Results with Current Shelf Status ---
    const mergedBooks = searchBooks.map((book) => {
        if (mapOfIdToBooks.has(book.id)) {
            return mapOfIdToBooks.get(book.id); // Return the book from main state (with current shelf)
        } else {
            // If not on a shelf, explicitly set to 'none' for the dropdown
            return { ...book, shelf: "none" };
        }
    });

    return (
        <div className="app">
            {/* The Routes component handles the conditional rendering based on URL */}
            <Routes>
                {/* Route 1: Main Page ("/") */}
                <Route 
                    path="/" 
                    element={
                        <MainPage 
                            books={books} 
                            updateBookShelf={updateBookShelf} 
                        />
                    } 
                />
                
                {/* Route 2: Search Page ("/search") */}
                <Route 
                    path="/search" 
                    element={
                        <SearchPage 
                            query={query}
                            setQuery={setQuery}
                            mergedBooks={mergedBooks}
                            updateBookShelf={updateBookShelf}
                        />
                    } 
                />
                
                {/* Route 3: Book Detail Page ("/book/:id") */}
                <Route 
                    path="/book/:id" 
                    element={<BookDetail updateBookShelf={updateBookShelf} />} 
                />
            </Routes>
        </div>
    );
};

export default BooksApp;
